import { faPaperclip as AttachmentIcon } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MDXEditor, type MDXEditorMethods } from '@mdxeditor/editor';
import { useColorScheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useAtom, useAtomValue } from 'jotai';
import { difference } from 'lodash';
import { useContext, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import Markdown from 'react-markdown';

import UserAvatar from '@/components/UserAvatar';
import {
  escapeStrikethrough,
  MARKDOWN_REMARK_PLUGINS
} from '@/constants/markdown.constants';
import {
  getAttachmentUploaderProps,
  isKanatiServiceTicket,
  KANATI_MAX_TEXT_LENGTH,
  KANATI_TEXT_TOO_LONG_MESSAGE
} from '@/features/TicketCreateDialog/TicketCreateDialog.constants';
import useConfirm from '@/hooks/useConfirm';
import {
  ticketAtom,
  useDeleteMessage,
  useUpdateMessage
} from '@/pages/Ticket/Ticket.store';
import { MDX_EDITOR_CONFIG } from '@/pages/Ticket/TicketCreate/TicketCreate.constants';
import TicketDetailsContext from '@/pages/Ticket/TicketDetails/TicketDetails.store';
import { axiosClient } from '@/services/BaseApi.service';
import { userAtom, userIsInternalAtom } from '@/store/user.store';
import { linkifyCaseIdsInHtml } from '@/utils/Common.utils';
import getDateTime from '@/utils/getDateTime.utils';

import FileUploader from '../FileUploader';
import MarkdownLink from '../MarkdownLink';
import { KANATI_RESPONSE_VISIBILITY_LABELS } from '../MessageInput/MessageInput.constants';
import useStyles from './Message.styles';
import type { MessageAttachment, MessageProps } from './Message.types';

const Message = (props: MessageProps) => {
  const { message } = props;
  const {
    attachments,
    body,
    created_at,
    id: messageId,
    internal,
    updated_at,
    user
  } = message;

  const form = useForm({
    defaultValues: {
      attachments: attachments || [],
      body: body || ''
    }
  });

  const { control, getValues, handleSubmit, setValue, clearErrors } = form;
  const bodyLength = (useWatch({ control, name: 'body' }) ?? '').length;

  const [userData] = useAtom(userAtom);
  const ticket = useAtomValue(ticketAtom);
  // the ticket atom can hold another ticket when a message is shown outside its own ticket page (e.g. case details)
  const isKanatiTicket =
    ticket.id === message.ticket_id &&
    isKanatiServiceTicket(ticket.ticket_type, ticket.ticket_category?.id);
  const userIsInternal = useAtomValue(userIsInternalAtom);
  const isKanatiLimited = isKanatiTicket && !userIsInternal;
  const kanatiResponseVisibility =
    userIsInternal &&
    'kanati_requested' in message &&
    message.kanati_requested === true
      ? message.kanati_response_visibility
      : null;
  const [isEditing, setIsEditing] = useState(false);
  const { classes } = useStyles();
  const mdxRef = useRef<MDXEditorMethods>(null);
  const { mode: themeMode } = useColorScheme();
  const { ConfirmationModal: MessageDeleteModal, confirm: confirmDelete } =
    useConfirm({
      title: 'Delete Message',
      message: 'Please confirm that you want to delete this message.',
      button: 'Delete'
    });

  const refetch = useContext(TicketDetailsContext);
  const deleteMessage = useDeleteMessage(refetch);

  const { isError: updateMessageError, mutate: updateMessage } =
    useUpdateMessage(refetch);
  const [isUpdateErrorDismissed, setIsUpdateErrorDismissed] = useState(false);
  const localUpdateMessageError = updateMessageError && !isUpdateErrorDismissed;

  const handleAttachmentClick = (message_id: number, file_key: string) => {
    const downloadAttachment = async () => {
      const response = await axiosClient.get(
        `/messages/${message_id}/attachments/${file_key}`
      );
      return window.open(response.data.download_url, '_blank');
    };
    return downloadAttachment();
  };

  const handleDeleteMessage = async () => {
    const confirmed = await confirmDelete();

    if (confirmed && messageId !== undefined) {
      deleteMessage.mutate(messageId);
    }
  };

  const isMessageDeleted = body === 'This message was deleted.';
  const isUsersMessage = userData?.email === user.email;

  const onSubmit = (data: { body: string }) => {
    try {
      const updatedMessage = { id: messageId, body: data.body, internal };

      // array of keys and ids of attachments in selectedValues
      const selectedValuesAttachments =
        attachments?.map((file) => ({
          key: file.key,
          id: file.id
        })) || [];

      // array of attachments in form
      const currentValueAttachments = getValues('attachments').map((file) => ({
        key: file.key,
        id: file.id
      }));

      // array of ids of attachments to be deleted
      const currentIds = currentValueAttachments
        .map((attachment) => attachment?.id)
        .filter((id): id is number => id !== undefined && id !== null);

      const deleteIds: number[] = difference(
        selectedValuesAttachments.map((file) => file.id),
        currentIds
      );

      // array of attachments to be added
      const addFiles: MessageAttachment[] = getValues('attachments').filter(
        (file) =>
          !selectedValuesAttachments.some(
            (selectedFile) => selectedFile.key === file.key
          )
      );

      setIsUpdateErrorDismissed(false);
      updateMessage({
        id: messageId,
        requestBody: updatedMessage,
        addFiles: addFiles?.length > 0 ? addFiles : [],
        deleteIds
      });

      setIsEditing(false);
    } catch (_error) {
      form.setValue('attachments', attachments ?? []);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    form.setValue('attachments', attachments ?? []);
    setIsEditing(false);
    setIsUpdateErrorDismissed(true);
  };

  const handleMessageChange = (value: string) => {
    clearErrors('body');
    setValue('body', value, { shouldDirty: true });
  };

  const messageDetails = (
    <Stack
      direction="row"
      className={classes.messageDetails}
      sx={{ alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Stack direction="row" className={classes.messageMetadata}>
        <Typography variant="body1" className={classes.messageAuthor}>
          {user.name}
        </Typography>
        <Typography variant="body2">{getDateTime(created_at)}</Typography>
        {internal && (
          <Chip
            color="warning"
            label="Internal"
            size="small"
            variant="outlined"
          />
        )}
        {message.human_soc_requested && (
          <Chip
            color="info"
            label="Human SOC requested"
            size="small"
            variant="outlined"
          />
        )}
        {kanatiResponseVisibility && (
          <Chip
            className={classes.kanatiRequestChip}
            label={`Kanati requested · ${KANATI_RESPONSE_VISIBILITY_LABELS[kanatiResponseVisibility].label}`}
            size="small"
            variant="outlined"
          />
        )}
        <Box className="messageControls">
          {!isMessageDeleted && isUsersMessage && !isEditing && (
            <>
              <Button color="primary" size="small" onClick={handleEdit}>
                Edit
              </Button>
              <Button color="error" size="small" onClick={handleDeleteMessage}>
                Delete
              </Button>
            </>
          )}
          {!isMessageDeleted && isUsersMessage && isEditing && (
            <>
              <Button color="primary" size="small" type="submit">
                Save
              </Button>
              <Button color="error" size="small" onClick={handleCancel}>
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Stack>
      {!isEditing && created_at !== updated_at && (
        <Typography variant="body2">
          <i>Last Edited: {getDateTime(updated_at)}</i>
        </Typography>
      )}
    </Stack>
  );

  const containsHtml = useMemo(
    () => /<\/?[a-z][\s\S]*>/i.test(body || ''),
    [body]
  );

  const escapedBody = useMemo(
    () => (containsHtml ? '' : escapeStrikethrough(body || '') || ' '),
    [body, containsHtml]
  );
  const linkedHtmlBody = useMemo(
    () =>
      containsHtml
        ? linkifyCaseIdsInHtml(
            body || '',
            userIsInternal ? '/case-internal/' : '/case/'
          )
        : '',
    [body, containsHtml, userIsInternal]
  );

  const messageBody = (
    <div>
      {/* Read Mode */}
      {!isEditing &&
        (containsHtml ? (
          <div
            className={classes.messageBody}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: renders message HTML with linkified URLs; not sanitized client-side, content comes from the backend
            dangerouslySetInnerHTML={{ __html: linkedHtmlBody }}
          />
        ) : (
          <Box className="markdown-body">
            <Markdown
              disallowedElements={['img']}
              remarkPlugins={MARKDOWN_REMARK_PLUGINS}
              components={{
                a: MarkdownLink
              }}
            >
              {escapedBody}
            </Markdown>
          </Box>
        ))}
      {!isEditing &&
        (message?.attachments?.length ?? 0) > 0 &&
        message.body !== 'This message was deleted.' &&
        message?.attachments?.map((attachment) => (
          <Stack
            key={attachment.key}
            sx={{ alignItems: 'flex-start' }}
            spacing={2}
          >
            <Button
              onClick={() => handleAttachmentClick(messageId, attachment.key)}
            >
              {attachment.file_name}
              <FontAwesomeIcon icon={AttachmentIcon} size="xs" />
            </Button>
          </Stack>
        ))}
      {/* Edit Mode */}
      {isEditing && (
        <FormControl fullWidth>
          <Controller
            control={control}
            name="body"
            rules={{
              required: true,
              validate: (value) =>
                !isKanatiLimited ||
                (value ?? '').length <= KANATI_MAX_TEXT_LENGTH ||
                KANATI_TEXT_TOO_LONG_MESSAGE
            }}
            render={({ field, formState }) => (
              <>
                <Box className={classes.messageEditorContainer}>
                  <MDXEditor
                    className={`${themeMode}-theme dark-editor ${classes.mdxEditor} ${formState.errors.body ? 'has-error' : ''}`}
                    contentEditableClassName="cm-richView"
                    ref={mdxRef}
                    markdown={field.value ?? ''}
                    onChange={handleMessageChange}
                    plugins={MDX_EDITOR_CONFIG}
                  />
                  {isKanatiLimited && (
                    <Typography
                      className={classes.messageCharacterCount}
                      variant="caption"
                      color={
                        bodyLength > KANATI_MAX_TEXT_LENGTH
                          ? 'error'
                          : 'text.secondary'
                      }
                    >
                      {bodyLength}/{KANATI_MAX_TEXT_LENGTH}
                    </Typography>
                  )}
                </Box>
                {formState.errors.body?.message && (
                  <FormHelperText error sx={{ mb: 1, mt: -0.5, ml: 1.5 }}>
                    {formState.errors.body.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </FormControl>
      )}
      {isEditing && message.body !== 'This message was deleted.' && (
        <div>
          <FileUploader {...getAttachmentUploaderProps(isKanatiTicket)} />
          {localUpdateMessageError && (
            <Typography sx={{ color: 'error.main' }}>
              * There was an error adding one or more attachments
            </Typography>
          )}
        </div>
      )}
    </div>
  );

  return (
    <ListItem className={classes.messageListItem}>
      <ListItemAvatar className={classes.messageListItemAvatar}>
        <UserAvatar name={user.name} pondurance={user.internal} size="lg" />
      </ListItemAvatar>
      {!isEditing && (
        <ListItemText
          className={classes.messageListItemText}
          disableTypography
          primary={messageDetails}
          secondary={messageBody}
        />
      )}
      {isEditing && (
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            style={{ width: '100%' }}
          >
            <ListItemText
              className={classes.messageListItemText}
              disableTypography
              primary={messageDetails}
              secondary={messageBody}
            />
          </form>
        </FormProvider>
      )}
      <MessageDeleteModal />
    </ListItem>
  );
};

export default Message;
